import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const STAGE_TONE = { won: "bg-emerald-100 text-emerald-700", lost: "bg-rose-100 text-rose-700", quoted: "bg-blue-100 text-blue-700" };
const SEV_TONE = { high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-600" };
const CAT_LABEL = { risk: "Risk", growth: "Growth", cross_sell: "Cross-sell", retention: "Retention" };

function Stat({ label, value, sub, tone = "" }) {
  return (
    <div className={`rounded-lg border border-slate-200 bg-white p-4 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="font-display text-xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export function OpportunitiesTab() {
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("all");
  const load = (s = stage) =>
    api.get(`/crm/opportunities?stage=${s}&limit=300`, { timeout: 60000 })
      .then((r) => setData(r.data)).catch(() => toast.error("Failed to load opportunities"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  if (!data) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;
  const st = data.stats;
  return (
    <div className="space-y-4" data-testid="crm-opps-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Open pipeline · KPI-029" value={fmtCurrency(st.open_value)} sub={`${fmtNumber(st.open_count)} open quotes`} />
        <Stat label="Win rate · KPI-030" value={`${st.win_rate_pct}%`} sub={`${fmtNumber(st.won_count)} won / ${fmtNumber(st.lost_count)} lost`} />
        <Stat label="Won value · KPI-031" value={fmtCurrency(st.won_value)} />
        <Stat label="Overdue quotes" value={fmtNumber(st.overdue_count)} sub="Past due date, still open" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Opportunities</span>
          <Select value={stage} onValueChange={(v) => { setStage(v); load(v); }}>
            <SelectTrigger className="w-[140px] h-8 text-xs bg-white" data-testid="crm-opps-stage-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {data.stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="ml-auto text-[11px] text-slate-400">Showing {data.total_shown} most recent</span>
        </div>
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-sm" data-testid="crm-opps-table">
            <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="text-left font-medium px-3 py-2">Ref</th>
                <th className="text-left font-medium px-3 py-2">Title</th>
                <th className="text-left font-medium px-3 py-2">Client</th>
                <th className="text-left font-medium px-3 py-2">Owner</th>
                <th className="text-left font-medium px-3 py-2">Stage</th>
                <th className="text-left font-medium px-3 py-2">Due</th>
                <th className="text-right font-medium px-3 py-2">Value</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((o) => (
                <tr key={o.id} className={`border-b border-slate-100 ${o.overdue ? "bg-rose-50/50" : ""}`}>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{o.ref_code || o.quote_number || o.job_number || "—"}</td>
                  <td className="px-3 py-2 text-slate-800 max-w-[280px] truncate">{o.title || "—"}</td>
                  <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate">{o.client_name || "—"}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{o.owner || "—"}</td>
                  <td className="px-3 py-2"><Badge className={`text-[10px] hover:bg-inherit ${STAGE_TONE[(o.stage || "").toLowerCase()] || "bg-slate-100 text-slate-600"}`}>{o.stage || "—"}</Badge></td>
                  <td className={`px-3 py-2 text-xs ${o.overdue ? "text-rose-600 font-medium" : "text-slate-500"}`}>{(o.due_date || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtCurrency(o.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ActivityTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/crm/activity", { timeout: 60000 }).then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load activity"));
  }, []);
  if (!data) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;
  const s = data.summary;
  return (
    <div className="space-y-4" data-testid="crm-activity-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Jobs created · 90d" value={fmtNumber(s.created_90d)} />
        <Stat label="Jobs completed · 90d" value={fmtNumber(s.completed_90d)} />
        <Stat label="Reactive · 90d" value={fmtNumber(s.reactive_90d)} />
        <Stat label="PPM · 90d" value={fmtNumber(s.ppm_90d)} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">Job volume — 6 months (reactive vs PPM)</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} width={40} />
                <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="reactive" stackId="a" fill="#e11d48" name="Reactive" />
                <Bar dataKey="ppm" stackId="a" fill="#0f4c75" name="PPM" />
                <Bar dataKey="other" stackId="a" fill="#94a3b8" name="Other" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-600 font-medium">Latest jobs (90d)</div>
          <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {data.recent.map((j) => (
              <div key={j.id} className="px-4 py-1.5 text-xs flex items-center gap-2">
                <span className="font-mono text-[9px] text-slate-400 w-16 truncate">{j.job_number || "—"}</span>
                <span className="flex-1 truncate text-slate-800">{j.title || "—"}</span>
                <span className="text-slate-400 truncate max-w-[120px]">{j.client_name}</span>
                <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[9px]">{j.work_category || j.status || "—"}</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SitesTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/crm/sites?limit=300", { timeout: 60000 }).then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load sites"));
  }, []);
  if (!data) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;
  const items = data.items.filter((s) => !q.trim() || (s.site || "").toLowerCase().includes(q.toLowerCase()) || (s.client || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="crm-sites-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Sites with job history · KPI-018" value={fmtNumber(data.total_sites)} />
        <Stat label="Active sites · 90d · KPI-019" value={fmtNumber(data.active_90d)} />
        <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search site or client…"
                 data-testid="crm-sites-search"
                 className="w-full text-sm outline-none placeholder:text-slate-400" />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto max-h-[560px] overflow-y-auto">
        <table className="w-full text-sm" data-testid="crm-sites-table">
          <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr className="border-b border-slate-200">
              <th className="text-left font-medium px-3 py-2">Site</th>
              <th className="text-left font-medium px-3 py-2">Client</th>
              <th className="text-right font-medium px-3 py-2">Jobs</th>
              <th className="text-right font-medium px-3 py-2">Open</th>
              <th className="text-right font-medium px-3 py-2">Reactive</th>
              <th className="text-left font-medium px-3 py-2">Last activity</th>
              <th className="text-right font-medium px-3 py-2">QR</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.site} className="border-b border-slate-100">
                <td className="px-3 py-2 text-slate-800 max-w-[300px] truncate">{s.site}</td>
                <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">{s.client || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtNumber(s.jobs)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${s.open ? "text-amber-700 font-medium" : "text-slate-400"}`}>{s.open}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-600">{s.reactive}</td>
                <td className="px-3 py-2 text-xs">
                  <span className={s.active_90d ? "text-emerald-600 font-medium" : "text-slate-400"}>{(s.last_activity || "").slice(0, 10) || "—"}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    data-testid={`crm-site-qr-${(s.site || "").slice(0, 20)}`}
                    className="text-[11px] text-blue-800 hover:underline"
                    onClick={async () => {
                      try {
                        const { data: lk } = await api.get(`/site-qr/lookup?name=${encodeURIComponent(s.site)}`);
                        const r = await api.get(`/site-qr/${lk.site_id}/poster.pdf`, { responseType: "blob" });
                        const u = URL.createObjectURL(r.data);
                        const el = document.createElement("a"); el.href = u; el.download = `GBE-Site-QR-${s.site}.pdf`; el.click();
                        URL.revokeObjectURL(u);
                      } catch {
                        toast.error("No site record matches this name — add it under CRM sites first");
                      }
                    }}>
                    Poster
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AIInsightsTab() {
  const { role } = usePermissions();
  const canGenerate = ["executive", "admin", "finance_admin", "ops_mgmt"].includes((role || "").toLowerCase());
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const load = () => api.get("/crm/insights").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/crm/insights/generate", {}, { timeout: 180000 });
      toast.success(`Generated ${r.data.generated} insights across ${r.data.clients_analysed} clients`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Insight generation failed");
    } finally { setGenerating(false); }
  };
  if (!data) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;
  return (
    <div className="space-y-4" data-testid="crm-ai-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Sparkles className="w-4 h-4 text-violet-600" />
        <h3 className="font-display text-base font-semibold text-slate-900">AI Insights · KPI-040/041</h3>
        <span className="text-[11px] text-slate-400">
          {data.last_generated ? `Last generated ${data.last_generated.slice(0, 16).replace("T", " ")} by ${data.generated_by}` : "Not generated yet"} · auto-runs Mondays 6am
        </span>
        {canGenerate && (
          <Button size="sm" className="h-8 ml-auto bg-violet-600 hover:bg-violet-700 text-white" onClick={generate}
                  disabled={generating} data-testid="crm-ai-generate-btn">
            {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            {generating ? "Analysing clients…" : "Generate now"}
          </Button>
        )}
      </div>
      {data.items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-sm text-slate-500" data-testid="crm-ai-empty">
          No insights yet — {canGenerate ? 'click "Generate now" to analyse your key and most active clients.' : "they generate automatically every Monday."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {data.items.map((i, idx) => (
            <div key={idx} className="bg-white border border-slate-200 rounded-lg p-4" data-testid="crm-ai-insight-card">
              <div className="flex items-center gap-2">
                <Badge className={`text-[10px] hover:bg-inherit ${SEV_TONE[i.severity] || SEV_TONE.low}`}>{i.severity}</Badge>
                <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 text-[10px]">{CAT_LABEL[i.category] || i.category}</Badge>
                <span className="ml-auto text-xs font-medium text-slate-700 truncate max-w-[180px]">{i.client}</span>
              </div>
              <p className="text-sm text-slate-800 mt-2">{i.insight}</p>
              {i.recommended_action && <p className="text-xs text-slate-500 mt-1.5"><span className="font-medium text-gbe">Action:</span> {i.recommended_action}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
